import { useState, useMemo } from 'react'
import { Box, Button, Flex, Grid, Text, Spinner } from '@chakra-ui/react'
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameDay,
  isSameMonth,
  isToday,
  addMonths,
  subMonths,
} from 'date-fns'
import { ptBR } from 'date-fns/locale'
import {
  LuBell,
  LuBellRing,
  LuCalendarCheck,
  LuCalendarPlus,
  LuCalendarX,
  LuCalendarPlus2,
  LuCalendarSearch,
  LuChevronLeft,
  LuChevronRight,
} from 'react-icons/lu'
import type { ElementType } from 'react'

import { useCalendar } from '@/hooks/calendar/useCalendar'
import { buildCalendarParams } from '@/hooks/calendar/useCalendarParams'
import { useEventModals } from '@/hooks/calendar/useEventModals'
import { STATUS_COLORS, STATUS_PALETTES, DEFAULT_EVENT_COLOR, HOLIDAY_COLOR, REMINDER_COLOR, REMINDER_COLOR_PALETTE } from '../constants'
import CalendarModals from './CalendarModals'
import type { CalendarFilterState } from './Filters'

// ─── Constants ────────────────────────────────────────────────────────────────

const STATUS_ICONS: Record<string, ElementType> = {
  prospection: LuCalendarPlus2,
  confirmed: LuCalendarCheck,
  cancelled: LuCalendarX,
  searched: LuCalendarSearch,
}

const WEEK_DAYS = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom']

// ─── Pure helpers ─────────────────────────────────────────────────────────────

const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1)

const formatEventTime = (time: string | null): string => {
  if (!time) return 'Dia\ntodo'
  const [hour, minute] = time.split(':')
  return minute === '00' ? `${hour}h` : `${hour}:${minute}h`
}

// ─── Sub-components ───────────────────────────────────────────────────────────

type MonthNavButtonProps = {
  direction: 'prev' | 'next'
  onClick: () => void
}

const MonthNavButton = ({ direction, onClick }: MonthNavButtonProps) => (
  <Box
    as="button"
    onClick={onClick}
    p={1}
    borderRadius="md"
    color="gray.500"
    _hover={{ bg: 'gray.100', color: 'gray.800' }}
  >
    {direction === 'prev' ? <LuChevronLeft size={20} /> : <LuChevronRight size={20} />}
  </Box>
)

type EventListItemProps = {
  event: {
    id: string
    agendaId: string | null
    eventId?: string
    name: string
    time: string | null
    colorPalette: string
    StatusIcon: React.ElementType | null
    subtitle: string | null
    hideTimeLabel?: boolean
  }
  onClick: () => void
}

const EventListItem = ({ event, onClick }: EventListItemProps) => (
  <Flex
    as="button"
    w="full"
    textAlign="left"
    align="center"
    px={4}
    py={3}
    borderBottom="1px solid"
    borderColor="gray.50"
    _last={{ borderBottom: 'none' }}
    _hover={{ bg: 'gray.50' }}
    cursor="pointer"
    onClick={onClick}
  >
    <Box
      bg={`${event.colorPalette}.subtle`}
      color={`${event.colorPalette}.600`}
      borderRadius="md"
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      flexShrink={0}
      w="52px"
      minH="52px"
      alignSelf="stretch"
      mr={3}
      gap={1.5}
      overflow="hidden"
      px="2px"
    >
      {event.StatusIcon && <event.StatusIcon size={16} />}
      {!event.hideTimeLabel && (
        <Text fontWeight="700" fontSize="0.75rem" textAlign="center" lineHeight="1" whiteSpace="pre-line">
          {formatEventTime(event.time)}
        </Text>
      )}
    </Box>

    <Box flex={1} minW={0}>
      <Text
        fontFamily="Montserrat"
        fontWeight="700"
        fontSize="0.9rem"
        color="gray.800"
        lineHeight="1.3"
        overflow="hidden"
        textOverflow="ellipsis"
        whiteSpace="nowrap"
      >
        {event.name}
      </Text>
      {event.subtitle && (
        <Text
          fontFamily="Montserrat"
          fontSize="0.75rem"
          color="gray.400"
          mt="2px"
          overflow="hidden"
          textOverflow="ellipsis"
        >
          {event.subtitle}
        </Text>
      )}
    </Box>
  </Flex>
)

// ─── Main component ───────────────────────────────────────────────────────────

type Props = {
  filters: CalendarFilterState
}

const CalendarMobileAgenda = ({ filters }: Props) => {
  const [currentMonth, setCurrentMonth] = useState(new Date())
  const [selectedDate, setSelectedDate] = useState(new Date())

  const { selectedEvent, newEventOpen, setNewEventOpen, openEvent, closeEvent } = useEventModals()

  const dateRange = useMemo(() => ({
    start: format(startOfMonth(currentMonth), 'yyyy-MM-dd'),
    end: format(endOfMonth(currentMonth), 'yyyy-MM-dd'),
  }), [currentMonth])

  const calendarParams = buildCalendarParams(filters, dateRange)
  const { data, isFetching } = useCalendar(calendarParams)

  const calendarDays = useMemo(() => {
    const start = startOfWeek(startOfMonth(currentMonth), { weekStartsOn: 1 })
    const end = endOfWeek(endOfMonth(currentMonth), { weekStartsOn: 1 })
    return eachDayOfInterval({ start, end })
  }, [currentMonth])

  const eventsByDate = useMemo(() => {
    const map: Record<string, { color: string }[]> = {}

    for (const event of data?.events ?? []) {
      const key = event.event_date
      if (!map[key]) map[key] = []
      map[key].push({ color: STATUS_COLORS[event.status] ?? DEFAULT_EVENT_COLOR })
    }

    for (const holiday of data?.holidays ?? []) {
      const key = holiday.date
      if (!map[key]) map[key] = []
      map[key].push({ color: HOLIDAY_COLOR })
    }

    for (const reminder of data?.reminders ?? []) {
      const key = reminder.reminder_date
      if (!map[key]) map[key] = []
      map[key].push({ color: REMINDER_COLOR })
    }

    return map
  }, [data])

  const selectedDayEvents = useMemo(() => {
    const dateKey = format(selectedDate, 'yyyy-MM-dd')
    const todayStr = format(new Date(), 'yyyy-MM-dd')

    const events = (data?.events ?? [])
      .filter((e) => e.event_date === dateKey)
      .map((e) => {
        const agenda = e.agenda as unknown as { id: string; name: string } | string | null
        const subtitle = typeof agenda === 'object' && agenda !== null ? agenda.name : agenda
        const agendaId = typeof agenda === 'object' && agenda !== null ? agenda.id : null
        return {
          id: e.id,
          agendaId,
          name: e.name,
          time: e.event_time ?? null,
          color: STATUS_COLORS[e.status] ?? DEFAULT_EVENT_COLOR,
          colorPalette: STATUS_PALETTES[e.status] ?? 'gray',
          StatusIcon: STATUS_ICONS[e.status] ?? null,
          subtitle: subtitle ?? null,
        }
      })

    const holidays = (data?.holidays ?? [])
      .filter((h) => h.date === dateKey)
      .map((h) => ({
        id: `holiday-${h.date}-${h.name}`,
        agendaId: null,
        name: h.name,
        time: null,
        color: HOLIDAY_COLOR,
        colorPalette: 'orange',
        StatusIcon: null,
        subtitle: 'Feriado',
      }))

    const reminders = (data?.reminders ?? [])
      .filter((r) => r.reminder_date === dateKey)
      .map((r) => ({
        id: `reminder-${r.id}`,
        agendaId: r.agenda_id,
        eventId: r.event_id,
        name: r.note ?? 'Lembrete',
        time: null,
        color: REMINDER_COLOR,
        colorPalette: REMINDER_COLOR_PALETTE,
        StatusIcon: dateKey === todayStr ? LuBellRing : LuBell,
        subtitle: 'Lembrete',
        hideTimeLabel: true,
      }))

    return [...events, ...holidays, ...reminders].sort((a, b) => {
      if (!a.time) return 1
      if (!b.time) return -1
      return a.time.localeCompare(b.time)
    })
  }, [data, selectedDate])

  const navigateMonth = (direction: 'prev' | 'next') => {
    const newMonth = direction === 'prev' ? subMonths(currentMonth, 1) : addMonths(currentMonth, 1)
    setCurrentMonth(newMonth)
    const today = new Date()
    setSelectedDate(isSameMonth(newMonth, today) ? today : startOfMonth(newMonth))
  }

  const monthTitleCapitalized = capitalize(format(currentMonth, 'MMMM yyyy', { locale: ptBR }))
  const dayNameCapitalized = capitalize(format(selectedDate, 'EEEE', { locale: ptBR }))

  return (
    <>
      <Box bg="white" borderRadius="xl" overflow="hidden">
        {/* Month navigation */}
        <Flex align="center" justify="space-between" px={4} pt={4} pb={2}>
          <Flex align="center" gap={2}>
            <Text fontFamily="Montserrat" fontWeight="700" fontSize="1rem" color="gray.800">
              {monthTitleCapitalized}
            </Text>
            {isFetching && <Spinner size="xs" color="primary.500" />}
          </Flex>
          <Flex gap={1}>
            <MonthNavButton direction="prev" onClick={() => navigateMonth('prev')} />
            <MonthNavButton direction="next" onClick={() => navigateMonth('next')} />
          </Flex>
        </Flex>

        {/* Week day headers */}
        <Grid templateColumns="repeat(7, 1fr)" px={2} pb={1}>
          {WEEK_DAYS.map((day) => (
            <Box key={day} textAlign="center" py={1}>
              <Text
                fontFamily="Montserrat"
                fontSize="0.6rem"
                fontWeight="700"
                color="primary.500"
                textTransform="uppercase"
                letterSpacing="0.05em"
              >
                {day}
              </Text>
            </Box>
          ))}
        </Grid>

        {/* Calendar grid */}
        <Grid templateColumns="repeat(7, 1fr)" px={2} pb={3}>
          {calendarDays.map((day) => {
            const dateKey = format(day, 'yyyy-MM-dd')
            const dots = eventsByDate[dateKey] ?? []
            const isCurrentMonth = isSameMonth(day, currentMonth)
            const isSelected = isSameDay(day, selectedDate)
            const isTodayDate = isToday(day)

            return (
              <Box
                key={dateKey}
                as="button"
                onClick={() => setSelectedDate(day)}
                display="flex"
                flexDirection="column"
                alignItems="center"
                py="3px"
                gap="2px"
                borderRadius="md"
                opacity={isCurrentMonth ? 1 : 0.3}
              >
                <Flex
                  w="28px"
                  h="28px"
                  align="center"
                  justify="center"
                  borderRadius="full"
                  bg={isSelected ? 'primary.500' : isTodayDate ? 'primary.100' : 'transparent'}
                >
                  <Text
                    fontFamily="Montserrat"
                    fontSize="0.78rem"
                    fontWeight="600"
                    color={isSelected ? 'white' : isTodayDate ? 'primary.500' : 'gray.600'}
                    lineHeight="1"
                  >
                    {format(day, 'd')}
                  </Text>
                </Flex>
                <Flex gap="2px" h="4px" align="center" minW="0">
                  {dots.slice(0, 3).map((dot, i) => (
                    <Box key={i} w="4px" h="4px" borderRadius="full" bg={dot.color} flexShrink={0} />
                  ))}
                </Flex>
              </Box>
            )
          })}
        </Grid>

        {/* Divider */}
        <Box h="1px" bg="gray.100" />

        {/* Selected day header */}
        <Flex align="center" justify="space-between" px={4} py={3}>
          <Flex align="center" gap={2}>
            <Text fontFamily="Montserrat" fontWeight="800" fontSize="1.5rem" color="gray.800" lineHeight="1">
              {format(selectedDate, 'd')}
            </Text>
            <Text fontFamily="Montserrat" fontWeight="600" fontSize="0.9rem" color="gray.500">
              {dayNameCapitalized}
            </Text>
            {selectedDayEvents.length > 0 && (
              <Text fontFamily="Montserrat" fontWeight="700" fontSize="0.7rem" color="primary.500" letterSpacing="0.06em">
                {selectedDayEvents.length} {selectedDayEvents.length === 1 ? 'EVENTO' : 'EVENTOS'}
              </Text>
            )}
          </Flex>
          <Button size="xs" colorPalette="primary" onClick={() => setNewEventOpen(true)} aria-label="Novo evento">
            <LuCalendarPlus />
          </Button>
        </Flex>

        {/* Events list */}
        <Box pb={4}>
          {selectedDayEvents.length === 0 ? (
            <Flex justify="center" py={6}>
              <Text fontFamily="Montserrat" fontSize="0.85rem" color="gray.400">
                Nenhum evento neste dia
              </Text>
            </Flex>
          ) : (
            <Flex direction="column">
              {selectedDayEvents.map((event) => (
                <EventListItem
                  key={event.id}
                  event={event}
                  onClick={() => {
                    if (event.agendaId) openEvent(event.agendaId, event.id)
                  }}
                />
              ))}
            </Flex>
          )}
        </Box>
      </Box>

      <CalendarModals
        selectedEvent={selectedEvent}
        newEventOpen={newEventOpen}
        agendas={data?.agendas ?? []}
        onCloseEvent={closeEvent}
        onCloseNew={setNewEventOpen}
        defaultDate={format(selectedDate, 'yyyy-MM-dd')}
      />
    </>
  )
}

export default CalendarMobileAgenda
